import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
import { teamSchema } from './team.js';
import { userSchema } from './user.js';
/**
 * L'identité renvoyée au client : le retrait de `hash` qu'exige `user.ts`, posé une fois.
 * `email` en moins aussi : la session ne s'en sert pas, et la tablette la range en IndexedDB.
 */
export const sessionUserSchema = userSchema.omit({ hash: true, email: true });
/**
 * De quoi afficher une équipe au choix, rien de plus. Pas d'`available` : une équipe
 * indisponible reste une équipe dont on peut endosser la session (M22).
 *
 * ⚠️ **`accountLogin` n'est pas décoratif : sans lui, deux équipes peuvent être indiscernables.**
 * `Team.name` est libre et dérivé des initiales des deux agents (M22) — deux équipes composées
 * des mêmes personnes portent donc le **même** nom, et le choix n'offre alors que deux boutons
 * identiques. Le `login` du compte partagé, lui, est déjà désambiguïsé à la création
 * (`buildTeamLogin` : `eq.pdlm`, puis `eq.pdlm2`) et unique en base : c'est le seul repère qui
 * départage à coup sûr deux homonymes.
 *
 * `null` quand l'équipe n'a pas (ou plus) de compte partagé rattaché — M22 lui en accorde
 * **0 ou 1**. Le rendre obligatoire ferait échouer la connexion d'un agent pour une tablette
 * détachée, ce qui ne l'empêche pas de partir en tournée.
 */
export const teamChoiceSchema = teamSchema
    .pick({ id: true, name: true })
    .extend({ accountLogin: z.string().min(1).nullable() });
/** `teamId` porte le second temps de la connexion : l'agent rappelle `/auth/login` (M6). */
export const loginRequestSchema = z.object({
    login: z.string().min(1),
    password: z.string().min(1),
    teamId: uuidV7Schema.optional(),
    /**
     * « Se souvenir de moi » — **coché par défaut**, c'est-à-dire le comportement d'avant : absent,
     * il vaut `true`. Décoché sur un poste de bureau, la session tient **12 h** au lieu de 7 jours et
     * son cookie meurt avec le navigateur (aucun `Max-Age`) : c'est le geste d'un poste partagé.
     *
     * ⚠️ **Sans effet au terrain** : la tablette doit pouvoir travailler hors réseau après sa
     * première connexion (D22), et raccourcir sa session l'y renverrait au mot de passe.
     */
    remember: z.boolean().optional(),
});
/**
 * ⚠️ `refreshToken` est **absent d'une session back-office** — il part en cookie httpOnly — et
 * présent pour le terrain. D'où l'`optional()` : c'est le canal qui varie, pas le contrat.
 * Pourquoi cet axe-là : `docs/ARCHITECTURE.md` D3.
 */
export const authenticatedSessionSchema = z.object({
    status: z.literal('authenticated'),
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1).optional(),
    user: sessionUserSchema,
    team: teamChoiceSchema.optional(),
});
/**
 * Le premier temps de la connexion d'un agent multi-équipes (M6) : **aucun jeton n'est émis**
 * tant que le périmètre est indécis.
 *
 * Le `min(2)` n'est pas décoratif : en dessous, la question ne se pose pas — une équipe unique
 * est résolue sans rien demander, et zéro équipe ouvre quand même la session (M6).
 */
export const teamSelectionRequiredSchema = z.object({
    status: z.literal('team_selection_required'),
    teams: z.array(teamChoiceSchema).min(2),
});
export const loginResponseSchema = z.discriminatedUnion('status', [
    authenticatedSessionSchema,
    teamSelectionRequiredSchema,
]);
/**
 * Les deux champs sont optionnels pour la même raison : ce que le client doit fournir dépend
 * de son front. `refreshToken` est absent pour le back-office — le cookie le porte (D3) — et
 * `teamId` n'a de sens que pour un agent de plusieurs équipes.
 *
 * ⚠️ `teamId` est **renvoyé par le client**, et c'est délibéré : `RefreshToken` ne porte pas
 * d'équipe (M23). Le serveur revérifie l'appartenance avant de signer — le client rappelle un
 * choix, il ne s'accorde pas un droit.
 */
export const refreshRequestSchema = z.object({
    refreshToken: z.string().min(1).optional(),
    teamId: uuidV7Schema.optional(),
});
/**
 * Un jeton d'accès neuf, et **l'équipe qu'il porte** : pas de rotation du refresh au démarrage
 * (M23, réserve `🔵`). Le refresh présenté reste valable — c'est ce qui évite d'éjecter un agent
 * dont plusieurs requêtes se rafraîchissent en parallèle à la reconnexion.
 *
 * ⚠️ **`team` est ce que le serveur a signé, pas ce que le client a demandé**, et c'est tout
 * l'objet de son ajout (T1) : quand le bureau change un agent d'équipe, la tablette rappelait
 * son ancienne équipe à chaque rafraîchissement et gardait le périmètre d'hier — il fallait se
 * déconnecter puis se reconnecter pour voir la nouvelle tournée. En rendant l'équipe résolue,
 * le serveur laisse la session locale se corriger seule.
 *
 * ⚠️ **Optionnelle, et `null` veut dire quelque chose** : `undefined` est la réponse d'un canal
 * qui n'a pas d'équipe à donner — le back-office —, `null` est un agent **sans équipe**, qui est
 * un état valide (M6). Les confondre ferait basculer un compte bureau dans le compartiment
 * « sans équipe » du terrain.
 */
export const refreshResponseSchema = z.object({
    accessToken: z.string().min(1),
    team: teamChoiceSchema.nullish(),
});
//# sourceMappingURL=session.js.map